/** @file quadHandles: the selected part's resize squares and pivot diamond, GPU-drawn at a CSS size. */
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { boneMaterial } from './boneMaterial';
import { displayPoint } from './displayPoint';
import { GIZMO_PALETTE } from './gizmoPalette';
import { matrixRows, type V3 } from './matrixRows';
import { QUAD_HANDLE_METRICS as M } from './quadHandleMetrics';
import { thinBatch } from './thinBatch';
import type { QuadHandle } from './viewportCtx';
import { VIEWPORT_COLORS } from './viewportColors';
import { VIEWPORT_GROUPS } from './viewportGroups';
import { VIEWPORT_LAYERS } from './viewportLayers';

/**
 * The nine handle points (target px, `quadHandlePoints`), the hovered or dragged handle, the view, scene units per CSS
 * px, and `top`: where a `rotate: parent` part's quad reaches its parent joint (a small marker, not a handle), or null.
 */
export type QuadHandlesInput = {
  points: readonly (readonly [number, number])[]; hover: QuadHandle | null; view: View; px: number; top?: readonly [number, number] | null;
};

/** `update(input)` once per frame (null hides them); `count()` the handles drawn; `mesh` for tests. */
export type QuadHandles = { mesh: Mesh; update(input: QuadHandlesInput | null): void; count(): number };

const rgba = (c: Color3): number[] => [c.r, c.g, c.b, 1];
const RIM = [0.07, 0.08, 0.09, 1];
const SQUARE = rgba(Color3.FromHexString(VIEWPORT_COLORS.centre));
const PIVOT = rgba(Color3.FromHexString(VIEWPORT_COLORS.boneWire));
const HOVER = rgba(GIZMO_PALETTE.hover);
const TOP = rgba(Color3.FromHexString(VIEWPORT_COLORS.bone));

/** Scratch (display frame): the centre, the two screen axes of the square and the view's back vector. */
const T: V3 = [0, 0, 0];
const X: V3 = [0, 0, 0];
const Y: V3 = [0, 0, 0];
const B: V3 = [0, 0, 0];

/**
 * One thin-instanced unit plane in the gizmo group (drawn over the marks): per handle a dark rim then the face, screen
 * aligned (squares) or turned 45 degrees (the pivot diamond), sized in CSS px (`QUAD_HANDLE_METRICS`), the hovered or
 * dragged one in the gizmo's hover colour; a `rotate: parent` part also gets a small bone-coloured diamond at its
 * parent-joint end (`top`).
 */
export function quadHandles(scene: Scene): QuadHandles {
  const mesh = CreatePlane('viewport-quad-handles', { size: 1 }, scene);
  Object.assign(mesh, { material: boneMaterial(scene), layerMask: VIEWPORT_LAYERS.overlay, renderingGroupId: VIEWPORT_GROUPS.gizmo, isPickable: false, alwaysSelectAsActiveMesh: true });
  const batch = thinBatch(mesh, 32);
  const put = (m: QuadHandlesInput, p: readonly [number, number], side: number, turned: boolean, color: readonly number[]): void => {
    const { right: r, up: u } = m.view.basis;
    const s = side * m.px * (turned ? Math.SQRT1_2 : 1);
    const t = turned ? 1 : 0;
    // world -> display frame: (x, y, z) -> (x, z, y); turned: the axes rotated 45 degrees (r + u, u - r)
    for (const [k, w] of [[0, 0], [1, 2], [2, 1]] as const) {
      X[k] = (r[w] + t * u[w]) * s;
      Y[k] = (u[w] - t * r[w]) * s;
    }
    displayPoint(T, p[0], p[1], m.view);
    matrixRows(batch.matrices(), batch.add(color), X, Y, B, T);
  };
  return {
    mesh,
    update: (m) => {
      batch.reset();
      if (m !== null) {
        const { back } = m.view.basis;
        [B[0], B[1], B[2]] = [back[0], back[2], back[1]];
        m.points.forEach((p, h) => {
          const pivot = h === 8;
          const side = pivot ? M.diamond / Math.SQRT2 : M.square;
          put(m, p, side + 2 * M.border, pivot, RIM);
          put(m, p, side, pivot, h === m.hover ? HOVER : pivot ? PIVOT : SQUARE);
        });
        if (m.top !== undefined && m.top !== null) {
          put(m, m.top, M.marker / Math.SQRT2 + 2 * M.border, true, RIM);
          put(m, m.top, M.marker / Math.SQRT2, true, TOP);
        }
      }
      batch.commit();
    },
    count: () => batch.count() / 2,
  };
}
